import { Chip } from "@mui/material";
import { DateTime } from "luxon";
import StarsBirthdayIcon from "../components/StarsBirthdayIcon";
import StarsErrorIcon from "../components/StarsErrorIcon";
import StarsWarningIcon from "../components/StarsWarningIcon";
import {
  CLASSIFICATION_SENIORITY_LIST_TYPE,
  EMPLOYEE_TEMP_LABEL,
  IAM_CONTRACT,
  NON_CONTRACT,
  SENIORITY_CRITICAL_MISSING_DATA,
  SENIORITY_MISSING_DATA,
  TWU_CONTRACT,
  WORKGROUP_MANAGER,
} from "./constants";
import {
  SENIORITY_BIRTHDAY_MESSAGE,
  SENIORITY_COLLISIONS_MESSAGE,
  SENIORITY_COLLISIONS_WARNING_MESSAGE,
  SENIORITY_MISSING_DATA_ERROR_HEADER,
  SENIORITY_MISSING_DATA_ERROR_MESSAGE,
  SENIORITY_MISSING_DATA_WARNING_HEADER,
  SENIORITY_MISSING_DATA_WARNING_MESSAGE,
  SENIORITY_MISSING_FTPT_WARNING_MESSAGE,
  SENIORITY_MISSING_PHONE_NUMBER_MESSAGE,
} from "./messageConstants";
import { padTime } from "./dateTimeFormatter";
import { getTimeZoneFromSwaStation } from "./timeZoneService";
import { sortSeniorityList } from "./filterSeniorityList";
import StarsPhoneIcon from "../components/StarsPhoneIcon";
import StarsCommentIcon from "../components/StarsCommentIcon";
import {
  useGridSelector,
  useGridApiContext,
  gridDetailPanelExpandedRowsContentCacheSelector,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from "@mui/x-data-grid-pro";
import React from "react";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
export const generateExportedFilename = ({
  station,
  workgroup,
  seniorityListType,
  generatedTimestamp,
  isAdmin,
  fileType,
}) => {
  let underScoredWorkgroup = workgroup.split(" ").join("_");
  let underScoredSeniorityListType = seniorityListType.split(" ").join("_");
  const date = DateTime.fromMillis(generatedTimestamp).setZone(getTimeZoneFromSwaStation(station));
  const underScoredDate = `${padTime(date.month)}-${padTime(date.day)}-${date.year}_${padTime(date.hour)}${padTime(
    date.minute
  )}`;
  if (fileType === "pdf") {
    return `STARS_${station}_SeniorityList_${
      !isAdmin ? "Emp_View_" : ""
    }${underScoredWorkgroup}_${underScoredSeniorityListType}_${underScoredDate}.pdf`;
  }

  return `STARS_${station}_SeniorityList_${underScoredWorkgroup}_${underScoredSeniorityListType}_${underScoredDate}.csv`;
};

export const isBirthday = (kronosBirthDateString) => {
  const today = DateTime.now();
  const givenDateTime = DateTime.fromFormat(kronosBirthDateString, "M/dd/yyyy");
  return today.month === givenDateTime.month && today.day === givenDateTime.day;
};

function CustomDetailPanelToggle(props) {
  const { id, value: isExpanded } = props;
  const apiRef = useGridApiContext();

  // To avoid calling ´getDetailPanelContent` all the time, the following selector
  // gives an object with the detail panel content for each row id.
  const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector);

  // If the value is not a valid React element, it means that the row has no detail panel.
  const hasDetail = React.isValidElement(contentCache[id]);

  return (
    <IconButton size="small" tabIndex={-1} disabled={!hasDetail} aria-label={isExpanded ? "Close" : "Open"}>
      <ExpandMoreIcon
        sx={{
          transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
          transition: (theme) =>
            theme.transitions.create("transform", {
              duration: theme.transitions.duration.shortest,
            }),
        }}
        fontSize="inherit"
      />
    </IconButton>
  );
}
export const getColumns = ({ seniorityList, contract, seniorityListType, station, workgroup, isEmployeeView }) => {
  let columns = [];
  let baseColumns = [
    {
      field: "rank",
      headerName: "Rank",
      type: "string",
      sortingOrder: ["asc", "desc"],
      renderCell: (params) => {
        if (params.row.isEmployeeEligibleForSeniorityListing) {
          return params.row.rank;
        } else {
          return "-";
        }
      },
      sortComparator: sortSeniorityList,
      flex: 0.12,
    },
    {
      field: "eid",
      headerName: "EID",
      type: "string",
      sortable: false,
      flex: 0.15,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      sortable: false,
      renderCell: (params) => {
        return (
          <div>
            {params.row.lastName}, {params.row.shortName}
            {params.row.isTemp && params.row.homeStation !== station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </div>
        );
      },
      flex: 0.3,
    },
    {
      field: "phoneNumber",
      headerName: "Phone #",
      type: "string",
      sortable: false,
      renderCell: (params) => {
        if (params.row.phoneNumber === "") {
          return <StarsPhoneIcon toolTipMessage={SENIORITY_MISSING_PHONE_NUMBER_MESSAGE} />;
        } else {
          return params.row.phoneNumber;
        }
      },
      flex: 0.15,
    },
    {
      field: "ftpt",
      headerName: "FT/PT",
      type: "string",
      sortable: false,
      renderCell: (params) => {
        if (params.row.ftpt === SENIORITY_CRITICAL_MISSING_DATA) {
          return (
            <StarsErrorIcon
              toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
              toolTipMessage={SENIORITY_MISSING_FTPT_WARNING_MESSAGE}
            />
          );
        } else {
          return params.row.ftpt;
        }
      },
      flex: 0.15,
    },
  ];

  let contractBasedColumns = [];
  if (contract) {
    if (contract === IAM_CONTRACT) {
      if (seniorityListType === CLASSIFICATION_SENIORITY_LIST_TYPE) {
        contractBasedColumns.push(
          ...[
            {
              field: "classificationDate",
              headerName: "Classification Date",
              type: "dateTime",
              sortable: false,
              renderCell: (params) => {
                if (params.row.classificationDate === SENIORITY_CRITICAL_MISSING_DATA) {
                  return (
                    <StarsErrorIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                    />
                  );
                } else if (params.row.classificationDate === SENIORITY_MISSING_DATA) {
                  return (
                    <StarsWarningIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                    />
                  );
                } else {
                  return params.row.classificationDate;
                }
              },
              flex: 0.25,
            },
            {
              field: "contractDate",
              headerName: "Contract Date",
              type: "dateTime",
              sortable: false,
              renderCell: (params) => {
                if (params.row.contractDate === SENIORITY_CRITICAL_MISSING_DATA) {
                  return (
                    <StarsErrorIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                    />
                  );
                } else if (params.row.contractDate === SENIORITY_MISSING_DATA) {
                  return (
                    <StarsWarningIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                    />
                  );
                } else {
                  return params.row.contractDate;
                }
              },
              flex: 0.25,
            },
            {
              field: "hireDate",
              headerName: "Hire Date",
              type: "dateTime",
              sortable: false,
              renderCell: (params) => {
                if (params.row.hireDate === SENIORITY_CRITICAL_MISSING_DATA) {
                  return (
                    <StarsErrorIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                    />
                  );
                } else if (params.row.hireDate === SENIORITY_MISSING_DATA) {
                  return (
                    <StarsWarningIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                    />
                  );
                } else {
                  return params.row.hireDate;
                }
              },
              flex: 0.25,
            },
            {
              field: "birthDate",
              headerName: "Birth Date",
              type: "dateTime",
              sortable: false,
              renderCell: (params) => {
                if (params.row.birthDate === SENIORITY_CRITICAL_MISSING_DATA) {
                  return (
                    <StarsErrorIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                    />
                  );
                } else if (params.row.birthDate === SENIORITY_MISSING_DATA) {
                  return (
                    <StarsWarningIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                    />
                  );
                } else {
                  if (isBirthday(params.row.birthDate)) {
                    return (
                      <div>
                        {params.row.birthDate} <StarsBirthdayIcon toolTipMessage={SENIORITY_BIRTHDAY_MESSAGE} />
                      </div>
                    );
                  }
                  return params.row.birthDate;
                }
              },
              flex: 0.25,
            },
          ]
        );
      } else {
        contractBasedColumns.push(
          ...[
            {
              field: "contractDate",
              headerName: "Contract Date",
              type: "dateTime",
              sortable: false,
              renderCell: (params) => {
                if (params.row.contractDate === SENIORITY_CRITICAL_MISSING_DATA) {
                  return (
                    <StarsErrorIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                    />
                  );
                } else if (params.row.contractDate === SENIORITY_MISSING_DATA) {
                  return (
                    <StarsWarningIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                    />
                  );
                } else {
                  return params.row.contractDate;
                }
              },
              flex: 0.25,
            },
            {
              field: "classificationDate",
              headerName: "Classification Date",
              type: "dateTime",
              sortable: false,
              renderCell: (params) => {
                if (params.row.classificationDate === SENIORITY_CRITICAL_MISSING_DATA) {
                  return (
                    <StarsErrorIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                    />
                  );
                } else if (params.row.classificationDate === SENIORITY_MISSING_DATA) {
                  return (
                    <StarsWarningIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                    />
                  );
                } else {
                  return params.row.classificationDate;
                }
              },
              flex: 0.25,
            },
            {
              field: "hireDate",
              headerName: "Hire Date",
              type: "dateTime",
              sortable: false,
              renderCell: (params) => {
                if (params.row.hireDate === SENIORITY_CRITICAL_MISSING_DATA) {
                  return (
                    <StarsErrorIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                    />
                  );
                } else if (params.row.hireDate === SENIORITY_MISSING_DATA) {
                  return (
                    <StarsWarningIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                    />
                  );
                } else {
                  return params.row.hireDate;
                }
              },
              flex: 0.25,
            },
            {
              field: "birthDate",
              headerName: "Birth Date",
              type: "dateTime",
              sortable: false,
              renderCell: (params) => {
                if (params.row.birthDate === SENIORITY_CRITICAL_MISSING_DATA) {
                  return (
                    <StarsErrorIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                    />
                  );
                } else if (params.row.birthDate === SENIORITY_MISSING_DATA) {
                  return (
                    <StarsWarningIcon
                      toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                      toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                    />
                  );
                } else {
                  if (isBirthday(params.row.birthDate)) {
                    return (
                      <div>
                        {params.row.birthDate} <StarsBirthdayIcon toolTipMessage={SENIORITY_BIRTHDAY_MESSAGE} />
                      </div>
                    );
                  }
                  return params.row.birthDate;
                }
              },
              flex: 0.25,
            },
          ]
        );
      }
    } else if (contract === TWU_CONTRACT) {
      contractBasedColumns.push(
        ...[
          {
            field: "wnSliSeniorityDate",
            headerName: "WN SLI Seniority Date",
            type: "dateTime",
            sortable: false,
            renderCell: (params) => {
              if (
                params.row.wnSliSeniorityDate === SENIORITY_CRITICAL_MISSING_DATA ||
                (params.row.wnSliSeniorityDate === "" &&
                  station === "ATL" &&
                  params.row.isTemp &&
                  params.row.homeStation !== station)
              ) {
                return (
                  <StarsErrorIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                  />
                );
              } else if (params.row.wnSliSeniorityDate === SENIORITY_MISSING_DATA) {
                return (
                  <StarsWarningIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                  />
                );
              } else {
                return params.row.wnSliSeniorityDate;
              }
            },
            flex: 0.25,
          },
          {
            field: "flAtlSeniorityDate",
            headerName: "FL ATL Seniority Date",
            type: "dateTime",
            sortable: false,
            renderCell: (params) => {
              if (params.row.flAtlSeniorityDate === SENIORITY_CRITICAL_MISSING_DATA) {
                return (
                  <StarsErrorIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                  />
                );
              } else if (params.row.flAtlSeniorityDate === SENIORITY_MISSING_DATA) {
                return (
                  <StarsWarningIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                  />
                );
              } else {
                if (params.row.flAtlSeniorityDate === "" && station !== "ATL") {
                  return "-";
                }
                return params.row.flAtlSeniorityDate;
              }
            },
            flex: 0.25,
          },
          {
            field: "dualSeniorityDate",
            headerName: "Dual Seniority Date",
            type: "dateTime",
            sortable: false,
            renderCell: (params) => {
              if (params.row.dualSeniorityDate === SENIORITY_CRITICAL_MISSING_DATA) {
                return (
                  <StarsErrorIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                  />
                );
              } else if (params.row.dualSeniorityDate === SENIORITY_MISSING_DATA) {
                return (
                  <StarsWarningIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                  />
                );
              } else {
                return params.row.dualSeniorityDate;
              }
            },
            flex: 0.25,
          },
          {
            field: "hireDate",
            headerName: "Hire Date",
            type: "dateTime",
            sortable: false,
            renderCell: (params) => {
              if (params.row.hireDate === SENIORITY_CRITICAL_MISSING_DATA) {
                return (
                  <StarsErrorIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                  />
                );
              } else if (params.row.hireDate === SENIORITY_MISSING_DATA) {
                return (
                  <StarsWarningIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                  />
                );
              } else {
                return params.row.hireDate;
              }
            },
            flex: 0.15,
          },
          {
            field: "birthDate",
            headerName: "Birth Date",
            type: "dateTime",
            sortable: false,
            renderCell: (params) => {
              if (params.row.birthDate === SENIORITY_CRITICAL_MISSING_DATA) {
                return (
                  <StarsErrorIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                  />
                );
              } else if (params.row.birthDate === SENIORITY_MISSING_DATA) {
                return (
                  <StarsWarningIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                  />
                );
              } else {
                if (isBirthday(params.row.birthDate)) {
                  return (
                    <div>
                      {params.row.birthDate} <StarsBirthdayIcon toolTipMessage={SENIORITY_BIRTHDAY_MESSAGE} />
                    </div>
                  );
                }
                return params.row.birthDate;
              }
            },
            flex: 0.15,
          },
        ]
      );
    } else if (contract === NON_CONTRACT) {
      contractBasedColumns.push(
        ...[
          {
            field: "seniorityDate",
            headerName: "Seniority Date",
            type: "dateTime",
            sortable: false,
            renderCell: (params) => {
              if (params.row.seniorityDate === SENIORITY_CRITICAL_MISSING_DATA) {
                return (
                  <StarsErrorIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                  />
                );
              } else if (params.row.seniorityDate === SENIORITY_MISSING_DATA) {
                return (
                  <StarsWarningIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                  />
                );
              } else {
                return params.row.seniorityDate;
              }
            },
            flex: 0.25,
          },
          {
            field: "hireDate",
            headerName: "Hire Date",
            type: "dateTime",
            sortable: false,
            renderCell: (params) => {
              if (params.row.hireDate === SENIORITY_CRITICAL_MISSING_DATA) {
                return (
                  <StarsErrorIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                  />
                );
              } else if (params.row.hireDate === SENIORITY_MISSING_DATA) {
                return (
                  <StarsWarningIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                  />
                );
              } else {
                return params.row.hireDate;
              }
            },
            flex: 0.25,
          },
          {
            field: "birthDate",
            headerName: "Birth Date",
            type: "dateTime",
            sortable: false,
            renderCell: (params) => {
              if (params.row.birthDate === SENIORITY_CRITICAL_MISSING_DATA) {
                return (
                  <StarsErrorIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_ERROR_MESSAGE}
                  />
                );
              } else if (params.row.birthDate === SENIORITY_MISSING_DATA) {
                return (
                  <StarsWarningIcon
                    toolTipHeader={SENIORITY_MISSING_DATA_WARNING_HEADER}
                    toolTipMessage={SENIORITY_MISSING_DATA_WARNING_MESSAGE}
                  />
                );
              } else {
                if (isBirthday(params.row.birthDate)) {
                  return (
                    <div>
                      {params.row.birthDate} <StarsBirthdayIcon toolTipMessage={SENIORITY_BIRTHDAY_MESSAGE} />
                    </div>
                  );
                }
                return params.row.birthDate;
              }
            },
            flex: 0.25,
          },
        ]
      );
    } else {
      console.log("error unrecognized contract: ", contract);
    }
  }

  let workgroupBasedColumns = [];

  if (workgroup === WORKGROUP_MANAGER) {
    workgroupBasedColumns.push({
      field: "jobTitle",
      headerName: "Job Title",
      type: "string",
      sortable: false,
      flex: 0.3,
    });
  }

  const extraColumns = [];

  const showTiebreakerTooltipColumn = seniorityList.some(
    (employee) =>
      (employee.seniorityTiebreakerDate && employee.seniorityTiebreakerStation === station) ||
      employee.isSeniorityCollision
  );
  if (showTiebreakerTooltipColumn) {
    extraColumns.push({
      headerName: "",
      type: "string",
      sortable: false,
      renderCell: (params) => {
        if (params.row.seniorityTiebreakerDate && params.row.seniorityTiebreakerStation === station) {
          return <StarsCommentIcon toolTipMessage={SENIORITY_COLLISIONS_MESSAGE} />;
        } else if (params.row.isSeniorityCollision) {
          return <StarsErrorIcon toolTipMessage={SENIORITY_COLLISIONS_WARNING_MESSAGE} />;
        }
      },
      flex: 0.1,
    });
  }

  columns.push(...baseColumns);
  columns.push(...workgroupBasedColumns);
  columns.push(...contractBasedColumns);
  columns.push(...extraColumns);

  if (isEmployeeView) {
    const exclusionList = ["birthDate", "phoneNumber"];
    const employeeColumns = columns.filter((item) => !exclusionList.includes(item.field));
    return employeeColumns;
  }
  return columns;
};

export const getMobileViewColumns = ({ station, isEmployeeView }) => {
  let columns = [];
  let baseColumns = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      renderCell: (params) => <CustomDetailPanelToggle id={params.id} value={params.value} />,
      width: 40,
      minWidth: 25,
    },
    {
      field: "rank",
      headerName: "Rank",
      type: "string",
      renderCell: (params) => {
        if (params.row.isEmployeeEligibleForSeniorityListing) {
          return params.row.rank;
        } else {
          return "-";
        }
      },
      width: 55,
      minWidth: 25,
      sortable: false,
    },
    {
      field: "eid",
      headerName: "EID",
      type: "string",
      sortable: false,
      width: 76,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      sortable: false,
      renderCell: (params) => {
        return (
          <div>
            {params.row.lastName}, {params.row.shortName}
            {params.row.isTemp && params.row.homeStation !== station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </div>
        );
      },
      minWidth: 140,
    },
    {
      field: "ftpt",
      headerName: "FT/PT",
      type: "string",
      sortable: false,
      renderCell: (params) => {
        if (params.row.ftpt === SENIORITY_CRITICAL_MISSING_DATA) {
          return (
            <StarsErrorIcon
              toolTipHeader={SENIORITY_MISSING_DATA_ERROR_HEADER}
              toolTipMessage={SENIORITY_MISSING_FTPT_WARNING_MESSAGE}
            />
          );
        } else {
          return params.row.ftpt;
        }
      },
      width: 60,
      minWidth: 25,
    },
  ];

  columns.push(...baseColumns);

  if (isEmployeeView) {
    const exclusionList = ["birthDate", "phoneNumber"];
    const employeeColumns = columns.filter((item) => !exclusionList.includes(item.field));
    return employeeColumns;
  }
  return columns;
};

export const getDetailPanelColumns = (contract, seniorityListType, station, workgroup) => {
  let columns = [];
  let contractBasedColumns = [];
  if (contract === IAM_CONTRACT) {
    if (seniorityListType === CLASSIFICATION_SENIORITY_LIST_TYPE) {
      contractBasedColumns.push(
        ...[
          {
            field: "classificationDate",
            headerName: "Classification Date",
          },
          {
            field: "contractDate",
            headerName: "Contract Date",
          },
          {
            field: "hireDate",
            headerName: "Hire Date",
          },
        ]
      );
    } else {
      contractBasedColumns.push(
        ...[
          {
            field: "contractDate",
            headerName: "Contract Date",
          },
          {
            field: "classificationDate",
            headerName: "Classification Date",
          },
          {
            field: "hireDate",
            headerName: "Hire Date",
          },
        ]
      );
    }
  } else if (contract === TWU_CONTRACT) {
    contractBasedColumns.push(
      ...[
        {
          field: "wnSliSeniorityDate",
          headerName: "WN SLI Seniority Date",
        },
        {
          field: "flAtlSeniorityDate",
          headerName: "FL ATL Seniority Date",
        },
        {
          field: "dualSeniorityDate",
          headerName: "Dual Seniority Date",
        },
        {
          field: "hireDate",
          headerName: "Hire Date",
        },
      ]
    );
  } else if (contract === NON_CONTRACT) {
    contractBasedColumns.push(
      ...[
        {
          field: "seniorityDate",
          headerName: "Seniority Date",
        },
        {
          field: "hireDate",
          headerName: "Hire Date",
        },
      ]
    );
  } else {
    console.log("error unrecognized contract: ", contract);
  }

  let workgroupBasedColumns = [];

  if (workgroup === WORKGROUP_MANAGER) {
    workgroupBasedColumns.push({
      field: "jobTitle",
      headerName: "Job Title",
    });
  }

  columns.push(...workgroupBasedColumns);
  columns.push(...contractBasedColumns);
  return columns;
};

export const getDetailColumnValues = (detailPanelColumns, seniorityRow) => {
  let result = [];
  detailPanelColumns.forEach(({ field, headerName }) => {
    if (seniorityRow.hasOwnProperty(field)) {
      if (seniorityRow[field].toString() !== "") result.push({ title: headerName, value: seniorityRow[field] });
      else result.push({ title: headerName, value: "-" });
    }
  });
  return result;
};
